import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Button, Checkbox, Input, Radio, Select, Space } from 'antd'

import i18n from 'i18next'

import CommonModal from '@/components/common/CommonModal'
import Info from '@/components/common/Info'

import { IColumn, IRow } from '@/interfaces/interfaces'

import { ActionProps, ActionTypes } from './types'

type MatchMode = 'exact' | 'contains' | 'starts' | 'ends'

// Values passed in when editing an existing delete_label action. onEditAction spreads
// `{...action, ...action.specs}`, so `terms` can arrive at the top level or under specs.
type DeleteLabelInitial = {
  axis?: 0 | 1
  terms?: string[]
  specs?: { terms?: string[] }
}

// `columns`/`rows` are the resulting (post-actions) output; `originalColumns`/`originalRows`
// are the untransformed table, used to restore saved labels this action itself deleted.
// `transposed` says whether the output orientation is swapped relative to the original
// table, so the saved selection is looked up in the correct original axis.
type DeleteByLabelProps = ActionProps<DeleteLabelInitial> & {
  originalColumns?: IColumn[]
  originalRows?: IRow[]
  transposed?: boolean
}

const RequiredLabel = ({ children }: { children: ReactNode }) => (
  <div style={{ marginBottom: 4, fontWeight: 500 }}>
    <span style={{ color: '#ff4d4f' }}>* </span>
    {children}
  </div>
)

const DeleteByLabel = ({
  columns = [],
  rows = [],
  originalColumns = [],
  originalRows = [],
  transposed = false,
  onSubmit,
  onCancel,
  isVisible,
  initialValues,
  clearOptions = false
}: DeleteByLabelProps) => {
  const iv = initialValues || {}
  const initialAxis = (iv.axis ?? 1) as 0 | 1
  // Memoized so it's a stable reference for the allNames useMemo deps below.
  const initialTerms = useMemo<string[]>(() => {
    const v = initialValues || {}
    return v.terms ?? v.specs?.terms ?? []
  }, [initialValues])

  const [axis, setAxis] = useState<0 | 1>(initialAxis)
  const [selected, setSelected] = useState<string[]>(initialTerms)
  const [matchMode, setMatchMode] = useState<MatchMode>('exact')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (clearOptions === true) {
      setAxis(initialAxis)
      setSelected(initialTerms)
      setMatchMode('exact')
      setSearch('')
    }
  }, [clearOptions, initialValues])

  useEffect(() => {
    setAxis(initialAxis)
    setSelected(initialTerms)
  }, [initialValues])

  // Selecting a label deletes every row/column carrying it, so list each label once.
  const allNames = useMemo(() => {
    const items = axis === 0 ? rows : columns
    const seen = new Set<string>()
    const names: string[] = []
    items.forEach(({ name }) => {
      if (!seen.has(name)) {
        seen.add(name)
        names.push(name)
      }
    })
    // When editing, this action already removed every row/column carrying a saved label,
    // so those labels are gone from the resulting output. Merge them back (validated
    // against the original table) so the saved selection stays visible/editable. Only for
    // the saved axis — switching axis clears the selection. Under transpose the output
    // axis maps to the opposite raw axis, so the saved labels live in the swapped array.
    if (axis === initialAxis) {
      const useOriginalColumns = (axis === 1) !== transposed
      const originalItems = useOriginalColumns ? originalColumns : originalRows
      const originalNames = new Set(originalItems.map(({ name }) => name))
      initialTerms.forEach(term => {
        if (!seen.has(term) && originalNames.has(term)) {
          seen.add(term)
          names.push(term)
        }
      })
    }
    return names
  }, [axis, rows, columns, originalRows, originalColumns, transposed, initialAxis, initialTerms])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return allNames
    return allNames.filter(name => {
      const value = name.toLowerCase()
      switch (matchMode) {
        case 'contains':
          return value.includes(term)
        case 'starts':
          return value.startsWith(term)
        case 'ends':
          return value.endsWith(term)
        default:
          return value === term
      }
    })
  }, [allNames, search, matchMode])

  const onAxisChange = (value: 0 | 1) => {
    setAxis(value)
    setSelected([])
    setSearch('')
  }

  const toggle = (name: string) =>
    setSelected(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    )

  const selectAll = () =>
    setSelected(prev => Array.from(new Set([...prev, ...filtered])))

  const clearAll = () => setSelected([])

  const submitForm = () => {
    onSubmit?.({
      actionType: ActionTypes.DELETE_LABEL,
      axis,
      specs: { terms: selected }
    })
  }

  const labelWord = axis === 0 ? 'Row' : 'Column'

  const popoverContent = (
    <div>
      <strong>Function</strong>
      <p>Delete rows or columns by their label from the output.</p>
      <strong>How to use</strong>
      <p>[Choose axis]: Select whether you want to delete columns or rows.</p>
      <p>
        [Search + match]: Filter the list by a search term using exact / contains / starts
        with / ends with, then tick the labels to delete (or use Select All).
      </p>
      <strong>NOTES</strong>
      <p>
        <em>
          Selecting a label removes every row/column that carries that label. Hierarchical
          labels match by leaf name, so all rows/columns sharing that leaf are removed
          regardless of parent — use Delete by position to remove a single one.
        </em>
      </p>
    </div>
  )

  return (
    <CommonModal
      visible={isVisible}
      onCancel={onCancel}
      onOk={submitForm}
      okDisabled={selected.length < 1}
      title={
        <span>
          Delete rows/columns by label <Info>{popoverContent}</Info>
        </span>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <RequiredLabel>Choose axis</RequiredLabel>
          <Select value={axis} onChange={onAxisChange} style={{ width: '100%' }}>
            <Select.Option value={1}>
              {i18n.t('editor.right-panel.data-source.form.columns')}
            </Select.Option>
            <Select.Option value={0}>
              {i18n.t('editor.right-panel.data-source.form.rows')}
            </Select.Option>
          </Select>
        </div>

        <div>
          <RequiredLabel>{`${labelWord} label(s)`}</RequiredLabel>
          <Input
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={'Search label, e.g. a prefix like "cust"'}
          />
        </div>

        <Radio.Group value={matchMode} onChange={e => setMatchMode(e.target.value)}>
          <Radio value="exact">Exact match</Radio>
          <Radio value="contains">Contains</Radio>
          <Radio value="starts">Starts with</Radio>
          <Radio value="ends">Ends with</Radio>
        </Radio.Group>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={selectAll} disabled={filtered.length === 0}>
            Select All
          </Button>
          <Button onClick={clearAll} disabled={selected.length === 0}>
            Clear All
          </Button>
        </div>

        <div
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 2,
            maxHeight: 180,
            overflowY: 'auto',
            padding: '8px 12px'
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ color: '#999' }}>No matching labels</div>
          ) : (
            filtered.map(name => (
              <div key={name} style={{ padding: '4px 0' }}>
                <Checkbox
                  checked={selected.includes(name)}
                  onChange={() => toggle(name)}
                >
                  {name}
                </Checkbox>
              </div>
            ))
          )}
        </div>
      </Space>
    </CommonModal>
  )
}

export default DeleteByLabel
